import { Button as AntButton, Checkbox, Dropdown as AntDropdown } from 'antd';
import type { CheckboxChangeEvent } from 'antd/lib/checkbox';
import type { ColumnsType, ColumnType } from 'antd/lib/table';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { t } from 'ttag';

const Dropdown = styled(AntDropdown)`
  & .ant-dropdown-menu {
    max-height: 600px;
    overflow-y: auto;
  }
`;

// Keep the button from increasing the size of the whole heading row
const Button = styled(AntButton)`
  padding: 0;
  height: unset;
`;

const EDIT_COLUMN_KEY = 'edit-column-dropdown';
// React.Key includes bigint, which cannot index a Record.
type Key = Exclude<React.Key, bigint>;

type VisibleColumnOptions = {
  /** Whether the column visibility dropdown is enabled */
  enabled?: boolean;
  /** List of columns that are visible by default - if null, all columns are visible */
  defaultVisibleColumns?: Key[] | null;
  /** List of columns that will always be visible, and will not be shown in dropdown */
  omitColumns?: Key[];
  /** List of columns that will always be visible, and will be shown as disabled item in dropdown */
  disabledColumns?: Key[];
  /**
   * Custom titles for columns, by key. This should be used to override existing titles or to add
   * titles for columns that don't have a title.
   */
  columnTitles?: Record<Key, string>;
};

const defaultVisibleColumnOptions: VisibleColumnOptions = {
  enabled: true,
  defaultVisibleColumns: null,
  omitColumns: ['reorder'],
  disabledColumns: [],
  columnTitles: {},
};

// ColumnsWithKeyType ensures that all columns have a key
type ColumnsWithKeyType<RecordType = unknown> =
  ColumnsType<RecordType> extends Array<infer Column> ? Array<Column & { key: Key }> : never;

function useVisibleColumns<RecordType = unknown>(
  columns: ColumnsWithKeyType<RecordType>,
  options?: VisibleColumnOptions,
): ColumnsType<RecordType> {
  const opts = { ...defaultVisibleColumnOptions, ...options };
  const [visibleKeys, setVisibleKeys] = useState(
    () => opts.defaultVisibleColumns ?? columns.map(col => col.key),
  );

  // This useEffect is for prop validation in development env
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production' && opts.enabled) {
      if (columns.some(col => !col.key)) {
        // eslint-disable-next-line no-console
        console.warn('[useVisibleColumns] All columns must have a key.');
      }

      if (columns.find(col => col.key === EDIT_COLUMN_KEY)) {
        // eslint-disable-next-line no-console
        console.warn(`[useVisibleColumns] Column key "${EDIT_COLUMN_KEY}" is reserved.`);
      }
    }
  }, [columns, opts.enabled]);

  const onChange = useCallback((event: CheckboxChangeEvent) => {
    const { value } = event.target;

    setVisibleKeys(prev =>
      prev.includes(value) ? prev.filter(key => key !== value) : [...prev, value],
    );
  }, []);

  const editColumn: ColumnType<RecordType> = useMemo(
    () => ({
      key: EDIT_COLUMN_KEY,
      align: 'center',
      width: 100,
      render: () => null,
      title: (
        <Dropdown
          trigger={['click']}
          menu={{
            selectable: true,
            multiple: true,
            selectedKeys: visibleKeys.map(String),
            items: columns
              .filter(column => !opts.omitColumns.includes(column.key))
              .map(column => {
                // antd also allows a render function for title. Its props carry sort and
                // filter state this dropdown has none of, and all of them are optional.
                const title = typeof column.title === 'function' ? column.title({}) : column.title;

                return {
                  key: column.key,
                  label: (
                    <Checkbox
                      key={column.key}
                      checked={visibleKeys.includes(column.key)}
                      value={column.key}
                      disabled={opts.disabledColumns?.includes(column.key)}
                      onChange={onChange}
                    >
                      {(opts.columnTitles[column.key] ?? title) || column.key}
                    </Checkbox>
                  ),
                };
              }),
          }}
        >
          <Button type="link">{t`Edit`}</Button>
        </Dropdown>
      ),
    }),
    [columns, visibleKeys, opts.omitColumns, opts.disabledColumns, opts.columnTitles, onChange],
  );

  const filteredColumns = useMemo(
    () => [
      ...columns.filter(col => [...opts.omitColumns, ...visibleKeys].includes(col.key)),
      editColumn,
    ],
    [columns, opts.omitColumns, visibleKeys, editColumn],
  );

  return opts.enabled ? filteredColumns : columns;
}

export default useVisibleColumns;
